import React, { useState, useEffect } from 'react';
import api from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowUpCircle, ArrowDownCircle, RefreshCw, Search, Package } from 'lucide-react';

export default function StockMovements() {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    loadMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadMovements is stable; only re-run on filter change
  }, [typeFilter]);

  const loadMovements = async () => {
    setLoading(true);
    try {
      const data = await api.getAllStockMovements(200, typeFilter || undefined);
      setMovements(data.movements || []);
    } catch (err) {
      console.error('Error loading movements:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredMovements = movements.filter(m => {
    if (!filter) return true;
    const s = filter.toLowerCase();
    return (m.product_name || '').toLowerCase().includes(s) ||
           (m.sku || '').toLowerCase().includes(s) ||
           (m.reference_number || '').toLowerCase().includes(s);
  });

  const movementTypeLabel = (type) => {
    const labels = {
      'sale': 'Sale',
      'po_receive': 'PO Received',
      'adjustment_in': 'Adjustment (+)',
      'adjustment_out': 'Adjustment (-)',
      'manual': 'Manual',
      'import': 'Import',
    };
    return labels[type] || type;
  };

  const movementColor = (type) => {
    if (['po_receive', 'adjustment_in', 'import'].includes(type)) return 'text-green-600';
    if (['sale', 'adjustment_out'].includes(type)) return 'text-red-600';
    return 'text-muted-foreground';
  };

  const totalIn = movements.filter(m => m.quantity_change > 0).reduce((s, m) => s + m.quantity_change, 0);
  const totalOut = movements.filter(m => m.quantity_change < 0).reduce((s, m) => s + Math.abs(m.quantity_change), 0);

  return (
    <div className="space-y-6" data-testid="stock-movements-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Stock Movement History</h1>
        <button onClick={loadMovements} className="flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md hover:bg-muted" data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 flex items-center gap-3">
            <ArrowUpCircle className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-xs text-muted-foreground">Total Stock In</p>
              <p className="text-xl font-bold text-green-600" data-testid="total-in">+{totalIn}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 flex items-center gap-3">
            <ArrowDownCircle className="h-8 w-8 text-red-500" />
            <div>
              <p className="text-xs text-muted-foreground">Total Stock Out</p>
              <p className="text-xl font-bold text-red-600" data-testid="total-out">-{totalOut}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 flex items-center gap-3">
            <Package className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-xs text-muted-foreground">Total Movements</p>
              <p className="text-xl font-bold" data-testid="total-movements">{movements.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text" placeholder="Search by product, SKU, or reference..."
                value={filter} onChange={(e) => setFilter(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border rounded-md bg-background"
                data-testid="search-input"
              />
            </div>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 text-sm border rounded-md bg-background" data-testid="type-filter">
              <option value="">All Types</option>
              <option value="sale">Sales</option>
              <option value="po_receive">PO Received</option>
              <option value="adjustment_in">Adjustment In</option>
              <option value="adjustment_out">Adjustment Out</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Movements table */}
      <Card>
        <CardHeader><CardTitle className="text-base">Movement Ledger</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading movements...</div>
          ) : filteredMovements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {movements.length === 0
                ? 'No stock movements recorded yet. Movements are tracked when POs are received, sales orders completed, or manual adjustments made.'
                : 'No movements match your search.'}
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="movements-table">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b text-muted-foreground text-left">
                    <th className="pb-2 font-medium">Date</th>
                    <th className="pb-2 font-medium">Product</th>
                    <th className="pb-2 font-medium">SKU</th>
                    <th className="pb-2 font-medium">Type</th>
                    <th className="pb-2 font-medium text-right">Change</th>
                    <th className="pb-2 font-medium text-right">Balance</th>
                    <th className="pb-2 font-medium">Reference</th>
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMovements.map((m, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-1.5 text-muted-foreground whitespace-nowrap">
                        {m.timestamp ? new Date(m.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="py-1.5 font-medium max-w-[180px] truncate">{m.product_name}</td>
                      <td className="py-1.5 text-muted-foreground">{m.sku}</td>
                      <td className="py-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          m.quantity_change > 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {movementTypeLabel(m.movement_type)}
                        </span>
                      </td>
                      <td className={`py-1.5 text-right font-bold ${movementColor(m.movement_type)}`}>
                        {m.quantity_change > 0 ? '+' : ''}{m.quantity_change}
                      </td>
                      <td className="py-1.5 text-right">{m.balance_after}</td>
                      <td className="py-1.5 text-muted-foreground">{m.reference_number || '-'}</td>
                      <td className="py-1.5 text-muted-foreground text-xs">{m.user_email || '-'}</td>
                      <td className="py-1.5 text-muted-foreground text-xs max-w-[150px] truncate">{m.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
